import { Flex, Text } from '@radix-ui/themes';
import { JsonViewer } from 'components/common/json/json-viewer';
import { CommonTableHoC } from 'components/common/table';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { RowWithID } from 'interfaces/tables/main';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { DaemonName } from 'lib_ts/interfaces/i-machine-daemons';
import { IReadyMsg } from 'lib_ts/interfaces/machine-msg/i-ready';
import { isNil } from 'lodash';
import { useMemo } from 'react';

interface IDaemonStateRow extends RowWithID {
  fieldName: string;
  fieldValue?: any;
}

interface IProps {
  r2f?: IReadyMsg;
  daemon: DaemonName;
  loading: boolean;
}

const COLUMNS: ITypedTableColumn<IDaemonStateRow>[] = [
  {
    label: 'admin.state',
    key: '_fieldName',
    formatFn: (m) => (
      <Flex direction="column" gap={RADIX.FLEX.GAP.XS} pl="1">
        <Text size="2">{m.fieldName}</Text>
      </Flex>
    ),
  },
  {
    label: 'admin.value',
    key: '_fieldValue',
    formatFn: (m) => {
      if (isNil(m.fieldValue)) {
        return null;
      }

      return (
        <Flex gap={RADIX.FLEX.GAP.XS} px="0" py="3">
          <JsonViewer value={m.fieldValue} />
        </Flex>
      );
    },
  },
];

/**
 * Renders the raw target_ms, curr_ms_comps, and full_state_dict values of a daemon, as returned by R2F ws messages
 */
export const DaemonStateTable = ({ r2f, daemon, loading }: IProps) => {
  const rows: IDaemonStateRow[] = useMemo(() => {
    const daemonState = r2f?.full_state?.[daemon];

    if (!daemonState) {
      return [];
    }

    return Object.keys(daemonState).map((key) => ({
      _id: key,
      fieldName: key,
      fieldValue: daemonState[key as keyof typeof daemonState],
    }));
  }, [r2f, daemon]);

  return (
    <CommonTableHoC
      dataTestID={`${daemon}-State`}
      identifier={`${daemon}-StateTable`}
      displayColumns={COLUMNS}
      displayData={rows}
      loading={loading}
    />
  );
};
